<template>
  <div class="flex w-full flex-col">
    <!-- Header Section -->
    <div class="flex items-center justify-between pb-8">
      <h1 class="text-5xl font-medium text-gray-900">{{ __('Orders') }}</h1>
      <button
        @click="handleExportAllOrders"
        class="btn btn-fill hover-lift-no-shadow disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="company_orders.length === 0"
      >
        <span class="text-white"> {{ __('Export All Orders') }}</span>
      </button>
    </div>

    <div
      class="flex w-full items-center justify-end"
      v-show="company_orders.length > 0"
    >
      <OrdersIndexFilterByPopup
        :filterBy="filterBy"
        @filterBy="handleFilterUpdate($event)"
      >
      </OrdersIndexFilterByPopup>
    </div>
    <!-- Stock Alert -->

    <!-- Table Container -->
    <div class="overflow-x-auto rounded-lg shadow-lg">
      <!-- Product Items -->
      <div
        class="scrollbar-thin mt-2 h-[600px] overflow-auto rounded-lg bg-white p-2 pr-4"
        style="scrollbar-gutter: stable"
      >
        <!-- Item 1 -->
        <template v-if="company_orders.length > 0">
          <div
            v-for="company_order in company_orders"
            :key="company_order.id"
            class="w-full"
            :class="isCompanyOrderUnfolded(company_order)"
          >
            <div
              class="grid grid-cols-2 items-center gap-y-4 border-b bg-white px-6 py-8 transition-all duration-200 hover:bg-gray-50 sm:grid-cols-9"
            >
              <div class="col-span-2 flex items-center gap-x-4 sm:col-span-2">
                <input
                  type="checkbox"
                  class="size-5 rounded-md border-gray-300 text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                  :checked="
                    selectedCompanyOrders.find(
                      (selectedCompanyOrder) =>
                        selectedCompanyOrder.id === company_order.id
                    )
                  "
                  @change="toggle(company_order.id)"
                />

                <img
                  class="block h-11 w-11 flex-shrink-0 rounded-full shadow-md"
                  :src="`/uploads/${company_order.order.user.profile_photo}`"
                  alt="User Avatar"
                  onerror="this.src='https://placehold.co/44x44/cccccc/ffffff?text=User'"
                />
                <span class="text-center font-semibold text-zinc-800">{{
                  company_order.order.user.first_name +
                  ' ' +
                  company_order.order.user.last_name
                }}</span>
              </div>

              <div
                class="col-span-1 flex items-center justify-center gap-2 sm:col-span-4"
              >
                {{ new Date(company_order.created_at).toLocaleString() }}
              </div>

              <div
                class="col-span-1 flex items-center justify-end sm:col-span-1 sm:justify-start"
              >
                <div class="flex items-center">
                  <FadeTransition>
                    <span
                      class="rounded-lg px-2 py-0.5 text-base capitalize"
                      :key="company_order.status"
                      :class="
                        company_order.status === 'pending'
                          ? 'bg-status-inactive/10 text-status-inactive'
                          : company_order.status === 'delivered'
                            ? 'bg-status-active/10 text-status-active'
                            : company_order.status === 'cancelled'
                              ? 'bg-error/10 text-error'
                              : 'bg-[#371066]/10 text-[#371066]'
                      "
                    >
                      {{ company_order.status }}
                    </span>
                  </FadeTransition>
                </div>
              </div>
              <!-- <div
                class="col-span-1 flex items-center justify-start pl-4 sm:col-span-2"
              >
                <button
                  @click.prevent="redirectToPage('messages')"
                  class="btn btn-outline hover-lift-no-shadow ring-1 ring-inset ring-primary hover:bg-primary/5"
                >
                  <span class="text-primary">{{ __('Send Message') }}</span>
                </button>
              </div> -->

              <div
                class="col-span-2 flex h-full items-center justify-end gap-x-4 sm:col-span-2"
              >
                <Icon
                  class="hover-lift-no-shadow cursor-pointer"
                  type="filled"
                  size="text-xl"
                  :name="
                    !isCompanyOrderUnfolded(company_order)
                      ? 'unfold_more'
                      : 'unfold_less'
                  "
                  @click.prevent="handleUnfoldCompanyOrder(company_order.id)"
                >
                </Icon>
              </div>
            </div>
            <template v-if="isCompanyOrderUnfolded(company_order)">
              <div
                v-for="(
                  typeOfOrderProdutVariant, type, index
                ) in company_order.order_product_variants"
                :key="index"
                class="col-span-3 grid min-h-32 grid-cols-3 border-b px-6 py-2 transition-all duration-200 hover:bg-gray-50 sm:col-span-9 sm:grid-cols-9"
                :class="
                  index ==
                  Object.keys(company_order.order_product_variants).length - 1
                    ? 'border-b-primary'
                    : 'border-b-gray-200'
                "
              >
                <div class="col-span-3 mb-2 flex flex-col sm:col-span-9">
                  <!-- <span class="font-medium"> {{ __('Details') }}: </span> -->
                  <div
                    class="mt-1 grid grid-cols-2 gap-2 text-sm text-zinc-600 sm:grid-cols-3"
                  >
                    <span class="font-semibold">
                      {{ __('Delivery type') }} :
                      <span class="font-medium capitalize text-zinc-600">
                        {{ type }}
                      </span>
                    </span>
                    <template
                      v-if="
                        type === 'offline' &&
                        typeOfOrderProdutVariant[0].order_shipping_detail
                      "
                    >
                      <span>
                        <strong> {{ __('Address: ') }}</strong>
                        {{
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .address
                        }}
                      </span>
                      <span
                        v-if="
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .detailed_address
                        "
                      >
                        <strong> {{ __('Detailed Address: ') }}</strong>
                        {{
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .detailed_address
                        }}
                      </span>

                      <span
                        v-if="
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .location
                        "
                      >
                        <strong> {{ __('Location: ') }}</strong>
                        {{
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .location
                        }}
                      </span>

                      <span
                        v-if="
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .email
                        "
                      >
                        <strong> {{ __('Email: ') }}</strong>
                        {{
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .email
                        }}
                      </span>
                      <span
                        v-if="
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .phone
                        "
                      >
                        <strong> {{ __('Phone: ') }}</strong>
                        {{
                          typeOfOrderProdutVariant[0].order_shipping_detail
                            .phone
                        }}
                      </span>
                    </template>

                    <template
                      v-if="
                        type === 'pickup' &&
                        typeOfOrderProdutVariant[0].product_variant?.product
                          ?.pickup_address
                      "
                    >
                      <span>
                        <strong> {{ __('Pick up address: ') }}</strong>
                        {{
                          typeOfOrderProdutVariant[0].product_variant.product
                            .pickup_address
                        }}
                      </span>
                    </template>
                  </div>
                </div>
                <div
                  v-for="(
                    order_product_variant, index
                  ) in typeOfOrderProdutVariant"
                  class="col-span-3 grid grid-cols-3 py-4 sm:col-span-9 sm:grid-cols-9"
                  :class="
                    index === typeOfOrderProdutVariant.length
                      ? 'border-b border-b-gray-200'
                      : ''
                  "
                >
                  <div
                    class="col-span-3 flex h-24 items-start gap-3 sm:col-span-6"
                  >
                    <div
                      @click.prevent="
                        redirectToPage(
                          'redesign.company.shop.products.show',
                          order_product_variant.product_variant.product.id
                        )
                      "
                      class="aspect-square cursor-pointer overflow-hidden rounded-lg shadow-md transition-transform duration-200 hover:shadow-lg"
                    >
                      <img
                        :src="`/uploads/${order_product_variant.product_variant.product.primary_image.path}`"
                        :key="
                          order_product_variant.product_variant.product
                            .primary_image.id
                        "
                        :alt="__('Product Image')"
                        onerror="this.src='https://placehold.co/44x44/cccccc/ffffff?text=Image'"
                        class="h-24 w-24 rounded-md border border-gray-200 object-cover"
                      />
                    </div>
                    <div class="flex h-full w-full flex-col space-y-2">
                      <div class="w-1/2 truncate font-medium text-zinc-600">
                        {{ order_product_variant.product_variant.prodname }}
                      </div>
                      <div
                        class="flex flex-row items-center justify-start gap-x-2"
                      >
                        <template
                          v-for="(attribute, index) in order_product_variant
                            .product_variant.formatted_attributes"
                        >
                          <div
                            v-if="attribute.name === 'Color'"
                            class="flex items-center justify-center gap-x-2 pr-2 text-sm font-medium text-zinc-600"
                            :class="
                              index ===
                              order_product_variant.product_variant
                                .formatted_attributes.length -
                                1
                                ? 'border-r-0'
                                : 'border-r border-zinc-200'
                            "
                          >
                            <span class="">{{ attribute.name }}: </span>
                            <div
                              class="h-6 w-6 rounded-full pl-2"
                              :style="{
                                backgroundColor: attribute.value.value
                              }"
                            ></div>
                          </div>

                          <div
                            v-else="!attribute.name === 'Color'"
                            class="pr-2 text-sm font-medium text-zinc-600"
                            :class="
                              index ===
                              order_product_variant.product_variant
                                .formatted_attributes.length -
                                1
                                ? 'border-r-0'
                                : 'border-r border-zinc-200'
                            "
                          >
                            {{ attribute.name }}: {{ attribute.value?.value }}
                          </div>
                        </template>
                      </div>

                      <div
                        class="w-1/2 truncate text-sm font-medium text-zinc-600"
                      >
                        {{ __('Quantity') }}:
                        {{ order_product_variant.quantity }}
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center sm:col-span-1">
                    <FadeTransition>
                      <span
                        :key="order_product_variant.status"
                        class="rounded-lg px-2 py-0.5 text-base capitalize transition-all duration-300"
                        :class="
                          order_product_variant.status === 'pending'
                            ? 'bg-status-inactive/10 text-status-inactive'
                            : order_product_variant.status === 'delivered'
                              ? 'bg-status-active/10 text-status-active'
                              : order_product_variant.status === 'cancelled'
                                ? 'bg-error/10 text-error'
                                : 'bg-[#371066]/10 text-[#371066]'
                        "
                      >
                        {{ order_product_variant.status }}
                      </span>
                    </FadeTransition>
                  </div>

                  <div
                    class="col-span-2 flex items-center justify-end sm:col-span-2"
                  >
                    <button
                      data-menu-variant-statuses-popup
                      class="btn btn-ghost relative flex items-center justify-center gap-x-2"
                      @click="
                        openOrderProductVariantMenuId =
                          openOrderProductVariantMenuId ===
                          order_product_variant.id
                            ? null
                            : order_product_variant.id
                      "
                    >
                      <Icon
                        name="edit"
                        type="filled"
                        class="text-primary"
                      ></Icon>

                      <span class="whitespace-nowrap text-primary">{{
                        __('Update Status')
                      }}</span>

                      <OrderVariantStatusPopup
                        :is-loading-change-status="isLoadingChangeStatus"
                        :order-product-variant-id="order_product_variant.id"
                        :is-open="
                          openOrderProductVariantMenuId ===
                          order_product_variant.id
                        "
                        :statuses="data.options.statuses"
                        @updateStatus="handleOrderVariantUpdateStatus"
                      ></OrderVariantStatusPopup>
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </template>

        <template v-else>
          <div
            class="flex h-full w-full items-center justify-center text-zinc-400"
          >
            {{ __('No orders found') }}
          </div>
        </template>

        <div
          v-if="
            company_orders.length > 0 &&
            data.company_orders.meta.current_page !==
              data.company_orders.meta.last_page
          "
          class="mt-auto flex w-full items-end justify-center py-2"
        >
          <button
            :disabled="isLoadingShowMore"
            class="hover-lift-no-shadow font-medium text-primary"
            :class="{ 'opacity-50': isLoadingShowMore }"
          >
            <div
              class="flex items-center justify-center gap-2"
              @click="
                handlePageChange({
                  page: data.company_orders.meta.current_page + 1,
                  reset: false
                })
              "
            >
              <span>{{ __('Show More') }}</span>
              <Icon
                v-if="isLoadingShowMore"
                class="animate-spin text-lg"
                name="progress_activity"
              />
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- start show more logic  -->
    <!-- Make show more logic for now -->
    <div class="relative h-28">
      <FadeTransition>
        <div
          v-show="selectedCompanyOrders.length"
          class="mt-2 flex w-full scroll-mb-24 flex-col items-center justify-between gap-4 rounded-lg bg-white px-6 py-4 shadow-lg sm:flex-row sm:gap-0"
        >
          <div
            class="flex w-full items-center justify-center gap-x-2 sm:justify-start"
          >
            <span class="px-4 py-2 text-zinc-600">
              {{ selectedCompanyOrders.length }} {{ __('selected') }}
            </span>

            <button
              class="hover-lift-no-shadow flex items-center justify-center gap-2 px-4 py-2"
              @click="
                selectedCompanyOrders.length == company_orders.length
                  ? (selectedCompanyOrders = [])
                  : (selectedCompanyOrders = company_orders.map((o) => ({
                      id: o.id,
                      updated_at: o.updated_at
                    })))
              "
            >
              <Icon name="fact_check" type="filled" class="text-primary"></Icon>
              <span class="text-sm font-medium text-primary">
                {{
                  selectedCompanyOrders.length === company_orders.length
                    ? __('Deselect all')
                    : __('Select All')
                }}
              </span>
            </button>
          </div>
          <div
            class="flex w-full items-center justify-center gap-x-2 sm:justify-end"
          >
            <button
              data-menu-company-orders-bulk
              @click="
                isUpdateOrderBulkStatusMenuOpen =
                  !isUpdateOrderBulkStatusMenuOpen
              "
              class="btn btn-fill hover-lift-no-shadow relative"
            >
              <div
                class="flex min-w-max flex-shrink-0 items-center justify-start gap-2"
              >
                <Icon name="create" type="filled" class="text-white"> </Icon>

                <span class="white text-sm font-medium">
                  {{ __('Update Status') }}
                </span>
              </div>
              <OrderVariantStatusPopupBulk
                :is-loading-change-status="isLoadingChangeStatus"
                @updateStatusBulk="handleCompanyOrderUpdateStatusBulk"
                :is-open="isUpdateOrderBulkStatusMenuOpen"
                :statuses="data.options.statuses"
              ></OrderVariantStatusPopupBulk>
            </button>
          </div>
        </div>
      </FadeTransition>
    </div>
  </div>
</template>
<script setup>
  import { nextTick, onMounted, ref } from 'vue';
  import InputToggle from '../../../components/form/input-toggle/input-toggle.vue';
  import Button from '../../../components/global/Button.vue';
  import Input from '../../../components/global/Form/Input.vue';
  import Icon from '@/components/global/Icon.vue';
  import FadeTransition from '@/components/global/FadeTransition.vue';
  import { __ } from '@/helpers';
  import OrderVariantStatusPopup from './components/OrderVariantStatusPopup.vue';
  import { useToast } from 'vue-toastification';
  import OrderVariantStatusPopupBulk from './components/OrderVariantStatusPopupBulk.vue';
  import OrdersIndexFilterByPopup from './components/OrdersIndexFilterByPopup.vue';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    }
  });

  const filterBy = ref(null);
  const company_orders = ref(props.data.company_orders.data);
  const data = ref(props.data);
  const toast = useToast();
  const isLoadingCompanyOrders = ref(false);
  const isLoadingShowMore = ref(false);

  const redirectToPage = (name, params) => {
    window.location.href = route(name, params);
  };
  const selectedCompanyOrders = ref([]);
  const unfoldedCompanyOrders = ref([]);

  const toggle = (companyOrderId) => {
    const company_order = company_orders.value.find(
      (item) => item.id === companyOrderId
    );

    if (
      selectedCompanyOrders.value.find((item) => item.id === companyOrderId)
    ) {
      selectedCompanyOrders.value = selectedCompanyOrders.value.filter(
        (selectedCompanyOrder) => selectedCompanyOrder.id !== companyOrderId
      );
    } else {
      selectedCompanyOrders.value.push({
        id: companyOrderId,
        updated_at: company_order.updated_at
      });
    }
  };
  const handleUnfoldCompanyOrder = (companyOrderId) => {
    const company_order = company_orders.value.find(
      (item) => item.id === companyOrderId
    );

    if (
      unfoldedCompanyOrders.value.find((item) => item.id === companyOrderId)
    ) {
      unfoldedCompanyOrders.value = unfoldedCompanyOrders.value.filter(
        (unfoldedCompanyOrder) => unfoldedCompanyOrder.id !== companyOrderId
      );
    } else {
      unfoldedCompanyOrders.value.push({
        id: companyOrderId,
        updated_at: company_order.updated_at
      });
    }
  };

  const isCompanyOrderUnfolded = (company_order) => {
    return unfoldedCompanyOrders.value.find(
      (unfoldedCompanyOrder) => unfoldedCompanyOrder.id === company_order.id
    );
  };

  const openOrderProductVariantMenuId = ref(null);
  onMounted(async () => {
    await nextTick();
    setupClickOutsideHandler();
    createOrderProductVariantsMap();
  });
  let clickOutsideHandler = null;
  const setupClickOutsideHandler = () => {
    clickOutsideHandler = (e) => {
      if (!e.target.closest('[data-menu-variant-statuses-popup]')) {
        openOrderProductVariantMenuId.value = null;
      }
      if (!e.target.closest('[data-menu-company-orders-bulk]')) {
        isUpdateOrderBulkStatusMenuOpen.value = false;
      }
    };
    document.addEventListener('click', clickOutsideHandler);
  };

  const isLoadingChangeStatus = ref({
    pending: false,
    delivered: false,
    cancelled: false,
    'in progress': false
  });

  const handleOrderVariantUpdateStatus = async ({
    orderProductVariantId,
    status_value
  }) => {
    isLoadingChangeStatus.value[status_value.id] = true;

    try {
      const order_variant = orderProductVariantById.get(orderProductVariantId);

      if (!order_variant) return;

      const response = await axios.patch(
        route(
          'redesign.company.shop.orders.order-product-variant.update-status',
          orderProductVariantId
        ),
        {
          status: status_value,
          updated_at: order_variant.updated_at
        }
      );

      toast.success(
        response?.data?.message ||
          __('Order Variant status modified successfully')
      );
      order_variant.status = response.data.status;
      order_variant.updated_at = response.data.updated_at;

      const company_order = company_orders.value.find(
        (co) => co.id === order_variant.company_order_id
      );
      company_order.updated_at = order_variant.updated_at;

      let allSameStatus = true;
      for (const type in company_order.order_product_variants) {
        for (const opv of company_order.order_product_variants[type]) {
          if (opv.status !== order_variant.status) {
            allSameStatus = false;
            break;
          }
        }
        if (!allSameStatus) break;
      }
      if (allSameStatus || order_variant.status === 'in progress') {
        company_order.status = order_variant.status;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingChangeStatus.value[status_value.id] = false;
    }
  };

  const handleCompanyOrderUpdateStatusBulk = async ({ status_value }) => {
    isLoadingChangeStatus.value[status_value.id] = true;
    try {
      if (!selectedCompanyOrders.value.length) return;
      const response = await axios.patch(
        route('redesign.company.shop.orders.update-status-bulk'),
        {
          status: status_value,
          company_orders: selectedCompanyOrders.value
        }
      );
      toast.success(
        response?.data?.message || __('Order status modified successfully')
      );
      response.data.updated_company_orders.forEach((updatedOrder) => {
        const company_order = company_orders.value.find(
          (co) => co.id === updatedOrder.id
        );
        if (company_order) {
          company_order.status = updatedOrder.status;
          company_order.updated_at = updatedOrder.updated_at;
        }

        for (const type in company_order.order_product_variants) {
          company_order.order_product_variants[type].forEach(
            (order_product_variant) => {
              order_product_variant.status = updatedOrder.status;
              order_product_variant.updated_at = updatedOrder.updated_at;
            }
          );
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingChangeStatus.value[status_value.id] = false;
      selectedCompanyOrders.value = [];
      isUpdateOrderBulkStatusMenuOpen.value = false;
    }
  };

  const handlePageChange = async ({ page, reset = false }) => {
    if (isLoadingCompanyOrders.value) return;

    if (reset) {
      data.value.company_orders.meta.current_page = 1;
      company_orders.value = [];
    }
    isLoadingShowMore.value = true;
    isLoadingCompanyOrders.value = true;
    try {
      const response = await axios.get(
        route('redesign.company.shop.orders.load-more', {
          page: page,
          filter: filterBy.value
        })
      );
      data.value.company_orders.meta = response.data.data.company_orders.meta;
      if (reset) {
        company_orders.value = response.data.data.company_orders.data;
      } else {
        company_orders.value.push(...response.data.data.company_orders.data);
      }
      createOrderProductVariantsMap();
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    } finally {
      isLoadingCompanyOrders.value = false;
      isLoadingShowMore.value = false;
    }
  };

  const isUpdateOrderBulkStatusMenuOpen = ref(false);
  const orderProductVariantById = new Map();
  const createOrderProductVariantsMap = () => {
    orderProductVariantById.clear();
    company_orders.value.forEach((company_order) => {
      for (const type in company_order.order_product_variants) {
        company_order.order_product_variants[type].forEach((variant) => {
          orderProductVariantById.set(variant.id, variant);
        });
      }
    });
  };

  const handleFilterUpdate = (event) => {
    filterBy.value = event;
    handlePageChange({
      reset: true,
      page: 1
    });
  };

  const handleExportAllOrders = async () => {
    try {
      redirectToPage('redesign.company.shop.orders.export');
    } catch (error) {
      toast.error(error.response?.data?.message || __('Something went wrong'));
    }
  };
</script>
